import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { onValue, ref } from "firebase/database";
import { db } from "../../../../lib/firebase";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import {
  getCreativesLayerizedPath,
  startSaveCreativesLayerized,
} from "../../../../actions/imageLayers";
import { hashStringToID } from "../../../../utils/string";
import { getUniqueID } from "../../../../utils/numbers";
import {
  LAYERS_NORMALIZED_MAX,
  LAYERS_UNDO_LIMIT,
  cloneLayers,
  isSameLayerBox,
  areLayerListsEqual,
  applyDisplayOrderToZIndex,
  layersArrayToRecord,
  layersRecordToArray,
  loadHtmlImage,
} from "../layers/layerGeometry";

const isEditableKeyboardTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    Boolean(target.isContentEditable)
  );
};

const isUndoHotkey = (event) =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  (event.code === "KeyZ" || event.key?.toLowerCase() === "z");

const useImageLayers = ({ sourceUrl, enabled }) => {
  const dispatch = useDispatchApp();
  const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
  const [layers, setLayers] = useState([]);
  const [canvasWidth, setCanvasWidth] = useState(null);
  const [canvasHeight, setCanvasHeight] = useState(null);
  const [creationTime, setCreationTime] = useState(null);
  const [layersDirty, setLayersDirty] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const undoStackRef = useRef([]);
  const selectedLayerIdRef = useRef(null);
  const layersDirtyRef = useRef(false);
  selectedLayerIdRef.current = selectedLayerId;
  layersDirtyRef.current = layersDirty;

  const clearUndoHistory = useCallback(() => {
    undoStackRef.current = [];
  }, []);

  const pushUndoSnapshot = useCallback((layersSnapshot) => {
    const last = undoStackRef.current[undoStackRef.current.length - 1];
    if (last && areLayerListsEqual(last.layers, layersSnapshot)) {
      return;
    }
    undoStackRef.current.push({
      layers: cloneLayers(layersSnapshot),
      selectedLayerId: selectedLayerIdRef.current,
      layersDirty: layersDirtyRef.current,
    });
    if (undoStackRef.current.length > LAYERS_UNDO_LIMIT) {
      undoStackRef.current.shift();
    }
  }, []);

  const urlHash = useMemo(
    () => (sourceUrl ? hashStringToID(sourceUrl) : null),
    [sourceUrl]
  );

  useEffect(() => {
    if (!enabled || !advertiserID || !urlHash) {
      clearUndoHistory();
      setLayers([]);
      setHasSnapshot(false);
      setLayersDirty(false);
      setSelectedLayerId(null);
      return undefined;
    }

    setIsLoadingSnapshot(true);
    const nodeRef = ref(
      db,
      getCreativesLayerizedPath({ advertiserID, urlHash })
    );

    const unsubscribe = onValue(nodeRef, (snapshot) => {
      setIsLoadingSnapshot(false);
      if (layersDirtyRef.current) {
        return;
      }

      if (!snapshot.exists()) {
        clearUndoHistory();
        setHasSnapshot(false);
        setLayers([]);
        setCanvasWidth(null);
        setCanvasHeight(null);
        setCreationTime(null);
        setLayersDirty(false);
        return;
      }

      const data = snapshot.val() || {};
      const nextLayers = layersRecordToArray(data.layers);
      clearUndoHistory();
      setHasSnapshot(true);
      setLayers(nextLayers);
      setCanvasWidth(data.canvasWidth || null);
      setCanvasHeight(data.canvasHeight || null);
      setCreationTime(data.creationTime || null);
      setLayersDirty(false);
      setSelectedLayerId((current) => {
        if (current && nextLayers.some((layer) => layer.id === current)) {
          return current;
        }
        const firstEditable = [...nextLayers]
          .reverse()
          .find((layer) => !layer.isBackground);
        return firstEditable?.id || nextLayers[0]?.id || null;
      });
    });

    return () => {
      unsubscribe();
    };
  }, [advertiserID, clearUndoHistory, enabled, urlHash]);

  const updateLayerBox = useCallback((layerId, box) => {
    let didChange = false;
    setLayers((current) => {
      const currentLayer = current.find((layer) => layer.id === layerId);
      if (!currentLayer || isSameLayerBox(currentLayer, box)) {
        return current;
      }
      didChange = true;
      pushUndoSnapshot(current);
      return current.map((layer) =>
        layer.id === layerId ? { ...layer, ...box } : layer
      );
    });
    if (didChange) {
      setLayersDirty(true);
    }
  }, [pushUndoSnapshot]);

  const reorderLayers = useCallback(
    (orderedDisplayIds) => {
      if (!Array.isArray(orderedDisplayIds) || orderedDisplayIds.length === 0) {
        return;
      }

      let didChange = false;
      setLayers((current) => {
        const nextLayers = applyDisplayOrderToZIndex(
          current,
          orderedDisplayIds
        );
        if (areLayerListsEqual(current, nextLayers)) {
          return current;
        }
        didChange = true;
        pushUndoSnapshot(current);
        return nextLayers;
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const renameLayer = useCallback(
    (layerId, name) => {
      const nextName = String(name || "").trim() || null;
      let didChange = false;
      setLayers((current) => {
        const currentLayer = current.find((layer) => layer.id === layerId);
        if (!currentLayer) return current;
        if ((currentLayer.name || "") === (nextName || "")) {
          return current;
        }
        didChange = true;
        pushUndoSnapshot(current);
        return current.map((layer) =>
          layer.id === layerId ? { ...layer, name: nextName } : layer
        );
      });
      if (didChange) {
        setLayersDirty(true);
      }
    },
    [pushUndoSnapshot]
  );

  const persistSnapshotNow = useCallback(
    async (targetUrl) => {
      const trimmedUrl = String(targetUrl || "").trim();
      if (!trimmedUrl || !layers.length) return { ok: false };

      return dispatch(
        startSaveCreativesLayerized({
          sourceUrl: trimmedUrl,
          snapshot: {
            canvasWidth,
            canvasHeight,
            layers: layersArrayToRecord(layers),
            creationTime: creationTime || Date.now(),
            originSourceUrl: sourceUrl || null,
          },
        })
      );
    },
    [
      canvasHeight,
      canvasWidth,
      creationTime,
      dispatch,
      layers,
      sourceUrl,
    ]
  );

  const addLayersFromUrls = useCallback(
    async (urls) => {
      const validUrls = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
      if (!validUrls.length) return;

      const prepared = [];
      for (const imageUrl of validUrls) {
        const image = await loadHtmlImage(imageUrl);
        prepared.push({ imageUrl, image });
      }

      let lastLayerId = null;
      setLayers((current) => {
        pushUndoSnapshot(current);
        let nextZIndex =
          current.reduce(
            (maxZ, layer) => Math.max(maxZ, Number(layer.zIndex) || 0),
            0
          ) + 1;
        const nextLayers = [...current];
        for (const { imageUrl, image } of prepared) {
          const naturalWidth = image.naturalWidth || LAYERS_NORMALIZED_MAX;
          const naturalHeight = image.naturalHeight || LAYERS_NORMALIZED_MAX;
          const aspect = naturalWidth / naturalHeight;
          const maxNorm = 400;
          const width = aspect >= 1 ? maxNorm : maxNorm * aspect;
          const height = aspect >= 1 ? maxNorm / aspect : maxNorm;
          const layerId = `imported_${getUniqueID()}`;
          lastLayerId = layerId;
          nextLayers.push({
            id: layerId,
            name: null,
            description: null,
            url: imageUrl,
            zIndex: nextZIndex,
            isBackground: false,
            x: (LAYERS_NORMALIZED_MAX - width) / 2,
            y: (LAYERS_NORMALIZED_MAX - height) / 2,
            width,
            height,
          });
          nextZIndex += 1;
        }
        return nextLayers;
      });
      if (lastLayerId) {
        setSelectedLayerId(lastLayerId);
      }
      setLayersDirty(true);
    },
    [pushUndoSnapshot]
  );

  const undoLastAction = useCallback(() => {
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    setLayers(previous.layers);
    setSelectedLayerId(previous.selectedLayerId);
    setLayersDirty(previous.layersDirty);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event) => {
      if (!isUndoHotkey(event) || isEditableKeyboardTarget(event.target)) {
        return;
      }
      if (!undoStackRef.current.length) return;
      event.preventDefault();
      undoLastAction();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, undoLastAction]);

  return {
    layers,
    canvasWidth,
    canvasHeight,
    layersDirty,
    selectedLayerId,
    setSelectedLayerId,
    updateLayerBox,
    addLayersFromUrls,
    reorderLayers,
    renameLayer,
    persistSnapshotNow,
    hasSnapshot,
    isLoadingSnapshot,
  };
};

export default useImageLayers;
